/**
 * ModalServiceAgreement - Web端实现
 *
 * 用于显示服务协议更新内容，用户需要阅读并确认协议更新
 * Web 端负责：Modal 容器、打开链接、按钮交互
 * 内容部分使用跨端公共组件 ServiceAgreementContent
 */

import React, { useCallback } from "react";
import { View, Text, Pressable, StyleSheet } from "../platformComponents";
import { ModalBase } from "@aspen/ui";
import { i18nCrossUtil } from "@adgm/libs";
import { legalRegulatoryUrl } from "@adgm/cross-config";
import { ServiceAgreementContent } from "./ServiceAgreementContent";
import { CommonButton } from "../common/CommonButton";
import { RightOutlined } from "@ant-design/icons";
import { TModalServiceAgreementProps } from "./index";

/**
 * ModalServiceAgreement Web端组件
 */
export const ModalServiceAgreement: React.FC<TModalServiceAgreementProps> = ({
  visible,
  onClose,
  onAgree,
}) => {
  const crossIntl = i18nCrossUtil.t();

  /**
   * 处理查看文档点击 - Web 端使用 window.open
   */
  const handleViewDocument = useCallback(() => {
    window.open(legalRegulatoryUrl, "_blank");
  }, []);

  /**
   * 处理同意并继续
   */
  const handleAgree = useCallback(() => {
    onAgree();
  }, [onAgree]);

  return (
    <ModalBase
      open={visible}
      onCancel={onClose}
      width={580}
      footer={null}
      title={null}
      centered
      closable={false}
      maskClosable={false}
      className="service-agreement-modal">
      {/* 容器：ModalBase 预设 padding: 24px 32px 32px，设计稿要求顶部32px，所以加pt-2（8px） */}
      <View style={styles.container}>
        {/* 使用跨端公共内容组件 */}
        <ServiceAgreementContent scrollViewHeight={335} titleMarginTop={0} />

        {/* View Document 链接 - Web 端特定 */}
        <Pressable style={styles.documentLink} onPress={handleViewDocument}>
          <Text style={styles.documentLinkText}>
            {crossIntl["agreement.service.update.view.document"]}
          </Text>
          <RightOutlined style={{ fontSize: 12, color: "#f17e00" }} />
        </Pressable>

        {/* 分割线 */}
        <View style={styles.divider} />

        {/* Agree and continue 按钮 */}
        <CommonButton
          onPress={handleAgree}
          text={crossIntl["agreement.service.update.agree.continue"]}
        />
      </View>
    </ModalBase>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: 8,
  },
  documentLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginTop: 24,
    cursor: "pointer" as any, // Web 特定样式
  },
  documentLinkText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: "DINPro-Medium",
    color: "#f17e00",
  },
  divider: {
    height: 1,
    width: "100%",
    backgroundColor: "#3D3F4B",
    marginVertical: 24,
  },
});

export type { TModalServiceAgreementProps };
